import { Link } from "react-router-dom";
import { ArrowRight, Briefcase, HardHat } from "lucide-react";
import Chapter from "./Chapter";
import { Reveal } from "./Reveal";
import { IMAGES, srcSetOf } from "../data/content";
import { useLang } from "../i18n";

const DIVISIONS = [
  { id: "white-collar", to: "/workforce#white-collar", Icon: Briefcase, img: "planning" },
  { id: "blue-collar", to: "/workforce#skilled", Icon: HardHat, img: "construction" },
];

// Three service divisions — the backbone of the site architecture.
export const DivisionsSection = ({ index = "02" }) => {
  const { t } = useLang();
  const d = t.divisions;

  return (
    <section className="mx-auto max-w-[1760px] px-5 py-20 sm:px-8 sm:py-28" data-testid="divisions-section">
      <Chapter index={index} label={d.label} title={d.title} lead={d.lead} />
      <div className="mt-12 grid gap-6 lg:grid-cols-2">
        {DIVISIONS.map((div, i) => {
          const copy = d.items[div.id];
          return (
            <Reveal key={div.id} delay={i * 0.1} className="h-full">
              <Link
                to={div.to}
                data-testid={`division-card-${div.id}`}
                className="group flex h-full flex-col border border-[#DDE4EA] bg-white transition-all duration-500 hover:-translate-y-1 hover:border-[#0F5C8C]/50 hover:shadow-[0_30px_70px_-45px_rgba(14,27,36,0.6)]"
              >
                <span className="relative block h-44 overflow-hidden border-b border-[#DDE4EA]">
                  <img
                    src={IMAGES[div.img]}
                    srcSet={srcSetOf(div.img)}
                    sizes="(max-width:1024px) 100vw, 420px"
                    alt=""
                    aria-hidden="true"
                    loading="lazy"
                    decoding="async"
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.2s] group-hover:scale-105"
                  />
                  <span className="absolute inset-0 bg-gradient-to-t from-[#0E1B24]/70 via-[#0E1B24]/25 to-transparent" />
                  <span className="absolute bottom-4 left-5 flex items-center gap-2 font-mono2 text-[10px] uppercase tracking-[0.22em] text-white">
                    <div.Icon size={14} /> {copy.kicker}
                  </span>
                </span>
                <span className="flex flex-1 flex-col p-7">
                  <h3 className="font-display text-xl font-extrabold leading-tight tracking-tight nv-head">{copy.title}</h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-[#41535F]">{copy.text}</p>
                  <ul className="mt-5 space-y-1.5 border-t border-[#DDE4EA] pt-5">
                    {copy.points.map((pt) => (
                      <li key={pt} className="flex gap-2 text-[12.5px] leading-relaxed text-[#41535F]">
                        <span className="mt-[7px] h-1 w-1 shrink-0 bg-[#0F5C8C]" /> {pt}
                      </li>
                    ))}
                  </ul>
                  <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#0F5C8C]">
                    {copy.cta} <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                </span>
              </Link>
            </Reveal>
          );
        })}
      </div>
    </section>
  );
};

export default DivisionsSection;
